'use client';

import { useEffect, useRef, useState } from 'react';
import { startTransition } from 'react';

const STUCK_DELAY_MS = 3000;

interface UseHandsFreeFallbackUIOptions {
  isRecording: boolean;
  responseMode: 'text' | 'voice';
  isAiVoiceEnabled: boolean;
  isConversationOver: boolean;
  isCurrentlyPaused: boolean;
  isSubmitting: boolean;
  isSpeaking: boolean;
  isListening: boolean;
  isStarting: boolean;
}

/** Snapshot of inputs so timeout callback can read current values. */
interface FallbackUIStateRef {
  isInActiveVoiceSession: boolean;
  isSubmitting: boolean;
  isSpeaking: boolean;
  isListening: boolean;
}

function getShouldConsiderStuck(state: FallbackUIStateRef): boolean {
  return (
    state.isInActiveVoiceSession &&
    !state.isSubmitting &&
    !state.isSpeaking &&
    !state.isListening
  );
}

/**
 * Shows "Having trouble listening? Tap to restore" only when the user is genuinely stuck:
 * in an active voice session, we should be listening, but we have not been listening for 3s.
 * Never shows when: paused, submitting, or AI speaking.
 */
export function useHandsFreeFallbackUI(options: UseHandsFreeFallbackUIOptions) {
  const {
    isRecording,
    responseMode,
    isAiVoiceEnabled,
    isConversationOver,
    isCurrentlyPaused,
    isSubmitting,
    isSpeaking,
    isListening,
  } = options;
  const [isStuckListening, setIsStuckListening] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isInActiveVoiceSession =
    isRecording &&
    responseMode === 'voice' &&
    isAiVoiceEnabled &&
    !isConversationOver &&
    !isCurrentlyPaused;

  const stateRef = useRef<FallbackUIStateRef>({
    isInActiveVoiceSession: false,
    isSubmitting: false,
    isSpeaking: false,
    isListening: false,
  });

  useEffect(() => {
    stateRef.current = {
      isInActiveVoiceSession,
      isSubmitting,
      isSpeaking,
      isListening,
    };
  }, [isInActiveVoiceSession, isSubmitting, isSpeaking, isListening]);

  useEffect(() => {
    const shouldConsiderStuck = getShouldConsiderStuck({
      isInActiveVoiceSession,
      isSubmitting,
      isSpeaking,
      isListening,
    });

    if (!shouldConsiderStuck) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (isStuckListening) {
        startTransition(() => setIsStuckListening(false));
      }
      return;
    }

    if (timeoutRef.current === null) {
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        const current = stateRef.current;
        if (getShouldConsiderStuck(current)) {
          startTransition(() => setIsStuckListening(true));
        }
      }, STUCK_DELAY_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    isInActiveVoiceSession,
    isSubmitting,
    isSpeaking,
    isListening,
    isStuckListening,
  ]);

  return {
    isStuckListening,
    setIsStuckListening,
  };
}
